/**
 * 클릭 시 확대/축소 토글이 되는 Figure 이미지 컴포넌트
 * - 기본: 기존 사이즈 유지
 * - 우측 하단 확대 아이콘 클릭: 원본 사이즈로 확대 (오버레이)
 * - 오버레이 클릭: 축소
 */
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import {
  FigureImage,
  FigureImageOverlay,
  FigureImageExpanded,
} from '../styles';

interface ExpandableFigureImageProps {
  src: string;
  alt: string;
  maxHeight?: number;
}

const ExpandIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21" fill="none">
    <g filter="url(#filter0_d_3059_4327)">
      <path d="M14.9763 13.9779L17.6013 16.6029M7.8513 8.72789H9.7263M9.7263 8.72789H11.6013M9.7263 8.72789V10.6029M9.7263 8.72789V6.85289M6.1638 2.55539C7.66087 1.69148 9.42094 1.40123 11.1161 1.7387C12.8113 2.07618 14.326 3.01837 15.3781 4.38975C16.4302 5.76114 16.9478 7.46821 16.8347 9.19295C16.7216 10.9177 15.9853 12.5425 14.7631 13.7647C13.5409 14.9869 11.9161 15.7232 10.1914 15.8363C8.46662 15.9494 6.75955 15.4318 5.38817 14.3797C4.01678 13.3276 3.07459 11.8129 2.73712 10.1177C2.39964 8.42252 2.68989 6.66246 3.5538 5.16539" stroke="#F4F4F4" strokeWidth="1.2" strokeLinecap="round"/>
    </g>
    <defs>
      <filter id="filter0_d_3059_4327" x="-0.898682" y="-0.898438" width="22" height="22" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
        <feFlood floodOpacity="0" result="BackgroundImageFix"/>
        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
        <feOffset dy="1"/>
        <feGaussianBlur stdDeviation="1"/>
        <feComposite in2="hardAlpha" operator="out"/>
        <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.15 0"/>
        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_3059_4327"/>
        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_3059_4327" result="shape"/>
      </filter>
    </defs>
  </svg>
);

const ExpandableFigureImage: React.FC<ExpandableFigureImageProps> = ({ src, alt, maxHeight }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(true);
  };

  const handleOverlayClick = () => {
    setIsExpanded(false);
  };

  const handleExpandedImageClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(false);
  };

  const wrapperStyle: React.CSSProperties = {
    position: 'relative',
    display: 'inline-block',
    width: '300px',
    height: maxHeight ? `${maxHeight}px` : '250px',
    maxWidth: '100%',
    aspectRatio: '300 / 250',
  };

  const imageStyle: React.CSSProperties = {
    display: 'block',
    width: '100%',
    height: '100%',
    objectFit: 'contain' as const,
  };

  const buttonStyle: React.CSSProperties = {
    position: 'absolute',
    bottom: '10px',
    right: '10px',
    width: '32px',
    height: '32px',
    borderRadius: '16px',
    background: 'rgba(0, 0, 0, 0.20)',
    boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.15)',
    backdropFilter: 'blur(8px)',
    border: 'none',
    outline: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    zIndex: 10,
  };
  

  return (
    <>
      <span style={wrapperStyle}>
        <FigureImage
          src={src}
          alt={alt}
          style={imageStyle}
        />
        <button
          onClick={handleExpandClick}
          aria-label="이미지 확대"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setIsExpanded(true);
            }
          }}
          style={buttonStyle}
        >
          <ExpandIcon />
        </button>
      </span>
      {isExpanded &&
        createPortal(
          <FigureImageOverlay
            onClick={handleOverlayClick}
            data-figure-image-overlay="true"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setIsExpanded(false);
              }
            }}
          >
            <FigureImageExpanded
              src={src}
              alt={alt}
              onClick={handleExpandedImageClick}
            />
          </FigureImageOverlay>,
          document.body
        )}
    </>
  );
};

export default ExpandableFigureImage;
